/* <cart-recommendations> — Frequently Bought Together in the cart.
   Fully JS-driven (the drawer opens later, so a small fetch delay is fine):
   fetches all candidate handles via ProductsByHandle (batched ≤20, beats the
   20/handle limit), aligns each card's variant to its source line item's size,
   drops cards whose matched size is out of stock, sorts, and renders the row.

   Markup (rendered by snippets/cart-recommendations.liquid):
     <cart-recommendations data-handles="h1,h2,…" data-sort="bestseller|price_desc"
                           data-context="cart" data-loading>
       <p class="cart-fbt__title">…</p>
       <div class="cart-fbt__track" data-fbt-track></div>
       <script type="application/json" data-fbt-source-map>{handle:{grouping,title}}</script>
     </cart-recommendations>
   data-context 'cart' uses the cart card; the source map is empty for the
   collection source (no size alignment then). */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rendered) return;
    this.dataset.rendered = 'true';
    this.track = this.querySelector('[data-fbt-track]');
    this.handles = (this.dataset.handles || '').split(',').map((h) => h.trim()).filter(Boolean);
    this.sortKey = this.dataset.sort || 'price_desc';
    this.context = this.dataset.context || 'cart';
    this.limit = parseInt(this.dataset.limit, 10) || 0;
    try {
      this.sourceMap = JSON.parse(this.querySelector('[data-fbt-source-map]')?.textContent || '{}');
    } catch {
      this.sourceMap = {};
    }
    this.#render();
  }

  async #render() {
    const api = window.ProductsByHandle;
    if (!this.track || !this.handles.length || !api) { this.remove(); return; }

    let cards = await api.fetchCardsByHandle(this.handles, this.context);
    cards = cards.filter((card) => this.#alignSize(card, api));
    cards = api.sortCards(cards, this.sortKey);
    if (this.limit > 0) cards = cards.slice(0, this.limit);

    if (!cards.length) { this.remove(); return; }
    this.track.replaceChildren(...cards);
    this.removeAttribute('data-loading');
    this.#initNav();

    // Klaviyo's BIS injection is disabled on the product template (its config
    // excludes /products/), so the standard .klaviyo-product-container in these
    // JS-injected cards never gets a working button there. On the PDP, point the
    // notify slot at the product page with ?highlight_klaviyo=1 (which auto-opens
    // that product's native BIS) — the same fallback the PDP's own product cards
    // use. Everywhere else the cards are injected after Klaviyo initialised, so
    // re-init BIS to bind its triggers (drawer + cart page).
    if (document.body.classList.contains('template-product')) {
      this.#applyPdpNotifyFallback();
    } else {
      window.resetKlaviyoBIS?.();
    }
  }

  /* PDP-only Klaviyo fallback (see #render). Swaps each horizontal card's
     .klaviyo-product-container contents for a link to the product page carrying
     ?variant=<selected>&highlight_klaviyo=1. The card's own variant-change logic
     (js--all-dawn.js) already shows/hides that container when an OOS size is
     picked, so we only keep the link's variant in sync. */
  #applyPdpNotifyFallback() {
    const label = window.klaviyo_bis?.collection_page_text || 'Notify me when available';
    for (const card of this.track.querySelectorAll('.product-card--horizontal')) {
      const container = card.querySelector('.klaviyo-product-container');
      const productUrl = card.dataset.productUrl;
      if (!container || !productUrl) continue;

      // Drop Klaviyo's hooks so any BIS scan ignores this (repurposed) node.
      container.removeAttribute('id');
      container.removeAttribute('data-klaviyo-handle');

      const select = card.querySelector('select.product-variant-id');
      const link = document.createElement('a');
      // --visible so it renders; the parent container's .hidden class (toggled by
      // the card's variant-change logic) is what gates its visibility.
      link.className = 'product-card__notify-me product-card__notify-me--visible button button--full-width button--primary';
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = label;

      const setHref = () => {
        link.href = `${productUrl}?variant=${select?.value || ''}&highlight_klaviyo=1`;
      };
      setHref();
      select?.addEventListener('change', setHref);
      container.replaceChildren(link);
    }
  }

  /* Prev/next arrows for users who can't swipe/trackpad-scroll. The nav is hidden
     unless the row actually overflows; arrows disable at the ends. A ResizeObserver
     re-evaluates when the track first gets width (e.g. when the cart drawer opens). */
  #initNav() {
    const nav = this.querySelector('[data-fbt-nav]');
    if (!nav || !this.track) return;
    const prev = nav.querySelector('[data-fbt-prev]');
    const next = nav.querySelector('[data-fbt-next]');
    const step = () => Math.max(this.track.clientWidth * 0.8, 180);

    prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));

    const update = () => {
      const overflow = this.track.scrollWidth - this.track.clientWidth > 2;
      nav.hidden = !overflow;
      if (!overflow) return;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    };

    this.track.addEventListener('scroll', update, { passive: true });
    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(this.track);
    } else {
      window.addEventListener('resize', update);
    }
    update();
  }

  /* Preselect the variant matching this card's source line item; return false
     to drop the card when that matched size is out of stock. Cards with no
     source entry (collection source, or handle not from a cart item) are kept
     with their default variant. */
  #alignSize(card, api) {
    const src = this.sourceMap[card.dataset.handle];
    if (!src) return true;

    let variants = [];
    try {
      variants = JSON.parse(card.querySelector('.product-variants-data-json')?.textContent || '{}').variants || [];
    } catch {
      return true;
    }
    const match = api.matchVariantBySize(variants, src);
    if (!match) return true;
    if (!match.available) return false;

    const select = card.querySelector('select.product-variant-id');
    if (select && select.value !== String(match.id)) {
      select.value = String(match.id);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }
}
if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);
